<template>
  <div
    class="forecast-widget"
    :style="{
      color: forecastColors.fontColor,
      borderRadius: typeof borderRadius === 'number' ? borderRadius + 'px' : borderRadius,
    }"
  >

    <div v-if="loading" class="loading">Loading forecast data...</div>
    <div v-else-if="!hasForecastData" class="no-data">Loading forecast data...</div>
    <div v-else class="forecast-content" :style="{ backgroundColor: forecastColors.backgroundColor }">
      <div
        v-for="(day, index) in forecastData.forecastday"
        :key="index"
        class="forecast-row"
      >
      <div  class="weather-icon"
          :style="{ width: `${iconSize}px`, height: `${iconSize}px` }">
        <img
          :src="getWeatherIcon(day.day.condition.code)"
          
           style="width: 100%; height: 100%"
          class="weather-icon-img"
          alt="icon"
        />
      </div>
        <span class="forecast-temp" :style="{ fontSize: tempSize + 'px' }">
          {{ Math.round(day.day.maxtemp_c) }}°
        </span>
        <span class="forecast-date" :style="{ fontSize: dateSize + 'px' }">
          {{ formatDate(day.date) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "ForecastWidget",
});
</script>

<script setup lang="ts">
import { computed, watch, onMounted, defineProps } from 'vue';
import { forecastData, forecastColors } from '@/eventBus';
import moment from 'moment';
import 'moment/locale/fr';

interface ForecastDay {
  date: string;
  day: {
    maxtemp_c: number;
    mintemp_c: number;
    condition: {
      text: string;
      code: number;
    };
  };
}

const props = defineProps<{
  loading: boolean;
  borderRadius: number;
  fontSize: number;
  config: any;
}>();

const hasForecastData = computed(() => {
  return forecastData.value && 
         forecastData.value.forecastday && 
         Array.isArray(forecastData.value.forecastday) && 
         forecastData.value.forecastday.length > 0;
});

function getWeatherIcon(conditionCode: number) {
  try {
    if (conditionCode === 1000) {
      return require("@/assets/images/weather/sunny.png");
    } else if (conditionCode == 1006) {
      return require("@/assets/images/weather/cloudy.png");
    } else if (conditionCode == 1009 || conditionCode == 1030 || conditionCode == 1147) {
      return require("@/assets/images/weather/overcast.png");
    } else if (conditionCode === 1003) {
      return require("@/assets/images/weather/partly_cloudy.png");
    } else if ([1273, 1252, 1249, 1246, 1240, 1201, 1198, 1195, 1192, 1189, 1186, 1183, 1180, 1153, 1150, 1063, 1289].includes(conditionCode)) {
      return require("@/assets/images/weather/rainy.png");
    } else if ([1264, 1261, 1258, 1255, 1243, 1225, 1222, 1219, 1216, 1213, 1210, 1066, 1114, 1117].includes(conditionCode)) {
      return require("@/assets/images/weather/snowy.png");
    } else if ([1237, 1168, 1069, 1072, 1279, 1282].includes(conditionCode)) {
      return require("@/assets/images/weather/ice.png");
    } else if ([1087, 1276, 1279, 1282].includes(conditionCode)) {
      return require("@/assets/images/weather/thunder_storm.png");
    } else if ([1204, 1207].includes(conditionCode)) {
      return require("@/assets/images/weather/windy.png");
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
}

function formatDate(date: string) {
  return moment(date).locale('fr').format('dddd, D MMM').replace(/^([a-zA-Z])/, (match) => match.toUpperCase());
}


const containerWidth = computed(() => props.config?.width || 300);
const tempSize = computed(() => {
  return Math.max(20, Math.min(25, containerWidth.value / 5));
});

const iconSize = computed(() => {
  return Math.max(40, Math.min(40, containerWidth.value / 2));
});

const dateSize = computed(() => {
  return Math.max(10, Math.min(18, containerWidth.value / 10));
});

watch(
  () => forecastData.value,
  (newVal) => {
    console.log("ForecastWidget: Received new forecast data:", newVal);
    if (newVal && newVal.forecastday && Array.isArray(newVal.forecastday)) {
      console.log("ForecastWidget: Valid forecast data received:", newVal.forecastday.length, "days");
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.forecast-widget {
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex !important;
  flex-direction: column;
  justify-content: flex-start;
}

.forecast-content {
  padding: 10px;
  display: flex !important;
  flex-direction: column;
  gap: 18px;
}

.forecast-row {
  display: flex !important;
  align-items: center;
  gap: 10px;
}

.forecast-icon {
  width: 40px !important;
  height: 40px !important;
  object-fit: contain !important;
}

.forecast-temp {
  font-weight: bold;
  min-width: 50px;
}

.forecast-date {
  margin-left: auto;
  font-size: 0.95em;
  opacity: 0.9;
}

.loading {
  display: flex !important;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #666;
}

.no-data {
  display: flex !important;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #666;
}



.weather-icon-img {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  display: block;
}

</style>
